import React, { useEffect, useRef, useState } from 'react';
import PageHeader from './PageHeader';
import clinicOrderFormsService from '../services/clinicOrderFormsService';
import { US_STATES } from '../data/usStates';
import { useT } from '../i18n/LanguageContext';
import {
  createDefaultsDraft,
  filterClinicOrderForms,
  formatDeliveryAddress,
  hasCompleteDeliveryAddress,
} from '../features/clinics/orderFormDefaults';
import './ClinicOrderForms.css';

export default function ClinicOrderForms({ onBack }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState(null);
  const [search, setSearch] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [busy, setBusy] = useState('loading');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [conflict, setConflict] = useState(false);
  const mounted = useRef(true);
  const formRef = useRef(null);
  const previewRef = useRef(null);
  const dirty = !!row && JSON.stringify(draft) !== JSON.stringify(createDefaultsDraft(row));

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const warn = e => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => { if (previewUrl) { previewRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); previewRef.current?.focus({ preventScroll: true }); } }, [previewUrl]);
  useEffect(() => {
    let active = true;
    clinicOrderFormsService.list().then(result => {
      if (active) { setData(result); setBusy(''); }
    }).catch(() => { if (active) { setError('Could not load clinic addresses. Try again.'); setBusy(''); } });
    return () => { active = false; };
  }, []);

  const discardOK = () => !dirty || window.confirm(t('Discard unsaved changes to this clinic?'));
  const select = next => {
    if (busy || !discardOK()) return;
    setRow(next); setDraft(createDefaultsDraft(next)); setPreviewUrl(''); setError(''); setMessage(''); setConflict(false);
  };
  const change = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value })); setPreviewUrl(''); setError(''); setMessage('');
  };
  const reload = async () => {
    if (!discardOK()) return;
    setBusy('loading'); setError('');
    try {
      const result = await clinicOrderFormsService.list();
      if (!mounted.current) return;
      setData(result);
      const next = result.clinics.find(c => c._id === row?._id);
      setRow(next || null); setDraft(next ? createDefaultsDraft(next) : null); setPreviewUrl(''); setConflict(false); setMessage('');
    } catch { if (mounted.current) setError('Could not load clinic addresses. Try again.'); }
    finally { if (mounted.current) setBusy(''); }
  };
  const run = async action => {
    if (busy || !formRef.current.reportValidity()) return;
    setBusy(action); setError(''); setMessage('');
    try {
      const payload = { revision: row.revision, defaults: draft };
      if (action === 'preview') {
        const blob = await clinicOrderFormsService.preview(row._id, payload);
        if (mounted.current) setPreviewUrl(URL.createObjectURL(blob));
      } else {
        const saved = await clinicOrderFormsService.save(row._id, payload);
        if (!mounted.current) return;
        setData(prev => ({ ...prev, clinics: prev.clinics.map(c => c._id === saved._id ? saved : c) }));
        setRow(saved); setDraft(createDefaultsDraft(saved)); setConflict(false);
        setMessage('Default delivery details saved. Future forms will use these defaults unless an order supplies an override.');
      }
    } catch (err) {
      if (!mounted.current) return;
      if (err.response?.status === 409) {
        setConflict(true); setPreviewUrl(''); setError('This clinic changed while you were editing. Reload its latest details before continuing.');
      } else if (err.response?.status === 400) {
        setError('Check the address and form fields. Use a valid US ZIP and characters the BLS PDF can display.');
      } else setError(action === 'preview' ? 'Could not generate the PDF preview. Your edits are still here; try again.' : 'Could not save defaults. Your edits are still here; try again.');
    } finally { if (mounted.current) setBusy(''); }
  };
  const field = (key, label, opts = {}) => (
    <label className="cof-field">
      <span>{t(label)}{opts.required && <span aria-hidden="true"> *</span>}</span>
      <input value={draft[key]} onChange={e => change(key, e.target.value)} {...opts} />
    </label>
  );
  const rows = filterClinicOrderForms(data?.clinics || [], {
    includeRemoved: showRemoved,
    search,
  });

  return <div className="clinic-order-forms">
    <PageHeader title={t('Order forms & addresses')} subtitle={t('Manage clinic delivery defaults and preview the BLS order form.')}>
      <button className="cof-button" disabled={!!busy} onClick={() => { if (discardOK()) onBack(); }}>{t('Back to clinics')}</button>
    </PageHeader>
    <p className="cof-intro">{t('Changes here update MedEffects delivery defaults. QuickBooks billing addresses and automatic order sending stay as configured.')}</p>
    {error && <div className="cof-error" role="alert">{t(error)}{(conflict || !data) && <button className="cof-button" disabled={!!busy} onClick={reload}>{t('Reload latest details')}</button>}</div>}
    {message && <p className="cof-success" role="status">{t(message)}</p>}
    {busy === 'loading' && <p role="status">{t('Loading clinic addresses...')}</p>}
    {data && <div className="cof-layout">
      <section className="cof-roster" aria-label={t('Clinic delivery addresses')}>
        <div className="cof-roster-tools">
          <label className="cof-field"><span>{t('Find a clinic or address')}</span><input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Search name, city or ZIP')} /></label>
          <div className="cof-roster-meta"><span>{t('{count} customers', { count: rows.length })}</span><label><input type="checkbox" checked={showRemoved} onChange={e => setShowRemoved(e.target.checked)} /> {t('Include removed')}</label></div>
        </div>
        <div className="cof-roster-list">
          {rows.map(c => <button key={c._id} className={`cof-clinic ${row?._id === c._id ? 'is-selected' : ''}`} aria-pressed={row?._id === c._id} disabled={!!busy} onClick={() => select(c)}>
            <strong>{c.name}</strong><span>{formatDeliveryAddress(c.defaults) || t('No default delivery address')}</span>
            <small className={c.needsIdentityReview || !hasCompleteDeliveryAddress(c.defaults) ? 'cof-attention' : ''}>{t(c.removed ? 'Removed' : c.needsIdentityReview ? 'Review clinic identity' : !hasCompleteDeliveryAddress(c.defaults) ? 'Address incomplete' : 'Address on file')}{!c.isActive && ` · ${t('Inactive')}`}{c.isPractice === false && ` · ${t('Hidden')}`}</small>
          </button>)}
          {!rows.length && <p className="cof-empty">{t('No matching customers.')}</p>}
        </div>
      </section>
      <section className="cof-editor" aria-label={t('Order form defaults')}>
        {!row ? <div className="cof-empty"><span className="material-symbols-outlined">location_on</span><h2>{t('Choose a clinic')}</h2><p>{t('Select a customer to review its delivery address and BLS form defaults.')}</p></div> : <>
          <div className="cof-editor-heading"><div><h2>{row.name}</h2><p>{t('Default delivery address')}</p></div><span className={`cof-badge ${dirty ? 'cof-attention' : ''}`}>{t(dirty ? 'Unsaved changes' : 'Saved defaults')}</span></div>
          {row.needsIdentityReview && <p className="cof-warning">{t('The clinic identity has changed since this address was recorded. Verify that these details belong to the selected clinic before saving.')}</p>}
          {row.removed && <p className="cof-warning">{t('This customer is removed. Restore it in Clinics before editing its defaults.')}</p>}
          <form ref={formRef} onSubmit={e => { e.preventDefault(); run('save'); }}>
            <fieldset disabled={!!busy || row.removed || conflict}>
              {field('facilityOrOffices', 'Facility / ship-to name', { required: true, maxLength: 120 })}
              {field('shipToAddress1', 'Address line 1', { required: true, maxLength: 120, autoComplete: 'address-line1' })}
              {field('shipToAddress2', 'Address line 2 (optional)', { maxLength: 120, autoComplete: 'address-line2' })}
              <div className="cof-address-row">
                {field('shipToCity', 'City', { required: true, maxLength: 80, autoComplete: 'address-level2' })}
                <label className="cof-field"><span>{t('State')} *</span><select required value={draft.shipToState} onChange={e => change('shipToState', e.target.value)} autoComplete="address-level1"><option value="">{t('Select state')}</option>{US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select></label>
                {field('shipToZip', 'ZIP code', { required: true, maxLength: 10, pattern: '[0-9]{5}(-[0-9]{4})?', autoComplete: 'postal-code' })}
              </div>
              <h3>{t('BLS form defaults')}</h3>
              <div className="cof-two-columns">
                {field('accountName', 'BLS account name', { maxLength: 120 })}
                {field('poShortName', 'Order form PO prefix', { maxLength: 80 })}
              </div>
              <p className="cof-help">{t('The PO prefix builds the vendor form number with the order date. Customer PO on an order is a separate field.')}</p>
              <div className="cof-two-columns">
                {field('terms', 'Terms', { maxLength: 80 })}
                <label className="cof-field"><span>{t('Default shipping service')}</span><select value={draft.defaultShippingSpeed} onChange={e => change('defaultShippingSpeed', e.target.value)}><option value="">{t('No clinic default')}</option>{data.shippingSpeeds.map(s => <option key={s}>{s}</option>)}</select></label>
              </div>
              <div className="cof-two-columns">
                <label className="cof-field"><span>{t('Default shipper')}</span><select value={draft.defaultPreferredCarrier} onChange={e => change('defaultPreferredCarrier', e.target.value)}>{(data.shippingCarriers || ['fedex', 'ups']).map(carrier => <option key={carrier} value={carrier}>{carrier === 'fedex' ? 'FedEx' : 'UPS'}</option>)}</select></label>
                <label className="cof-field"><span>{t('Signature instruction')}</span><select value={draft.defaultSignatureNote} onChange={e => change('defaultSignatureNote', e.target.value)}><option value="none">{t('No added instruction')}</option><option value="no_signature_required">{t('No signature required')}</option><option value="residential_no_signature">{t('Residential delivery — no signature required')}</option></select></label>
              </div>
              <p className="cof-help">{t('An order’s selected shipper and shipping service take precedence over these clinic defaults.')}</p>
              <div className="cof-actions">
                <button className="cof-button" type="button" onClick={() => run('preview')}>{t(busy === 'preview' ? 'Generating PDF...' : 'Preview BLS PDF')}</button>
                <button className="cof-button cof-primary" type="submit">{t(busy === 'save' ? 'Saving...' : 'Save defaults')}</button>
              </div>
            </fieldset>
          </form>
          <p className="cof-help cof-preview-help">{t('Preview uses the values shown above and one sample product. It does not save changes, create an order or send anything. Delivery dates and products come from each real order.')}</p>
          {row.confirmedAt && <p className="cof-help">{t('Address last confirmed: {date}', { date: new Date(row.confirmedAt).toLocaleString() })}</p>}
          {previewUrl && <section ref={previewRef} tabIndex={-1} className="cof-preview" aria-label={t('BLS PDF preview')}><div><h3>{t('BLS PDF preview')}</h3><a href={previewUrl} target="_blank" rel="noreferrer">{t('Open PDF in new tab')}</a></div><iframe title={t('BLS PDF preview')} src={previewUrl} /></section>}
        </>}
      </section>
    </div>}
  </div>;
}
